import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CircleAlert, CircleCheck, Pencil, PlugZap, Plus, Send } from 'lucide-react'
import type { NotificationProviderDriver } from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Empty,
  Input,
  Modal,
  PageHeader,
  Select,
  useAuth,
  useToast,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import { NOTIFICATIONS_KEY, useProviders, type Provider } from '../../lib/notifications'

interface FormState {
  id?: string
  driver: NotificationProviderDriver
  label: string
  config: Record<string, string>
  secrets: Record<string, string>
  secretsSet: string[]
}

/**
 * Fournisseurs email / SMS (ADR-010, ADR-035). 🔒 Secrets chiffrés, jamais
 * réaffichés (on ne peut que les remplacer) ; activation après un test réussi ;
 * un seul fournisseur actif par type. Sans fournisseur actif : SMTP du serveur
 * pour les courriels, SMS simulés hors production.
 */
export function ProvidersPage() {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const providers = useProviders()
  const [form, setForm] = useState<FormState | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const drivers = providers.data?.drivers
  const invalidate = () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY })

  const save = useMutation({
    mutationFn: (f: FormState) => {
      const secrets = Object.fromEntries(Object.entries(f.secrets).filter(([, v]) => v !== ''))
      return f.id
        ? session.api.patch(`/admin/notifications/providers/${f.id}`, {
            label: f.label,
            config: f.config,
            secrets,
          })
        : session.api.post('/admin/notifications/providers', {
            driver: f.driver,
            label: f.label,
            config: f.config,
            secrets,
          })
    },
    onSuccess: async () => {
      toast.success('Fournisseur enregistré')
      setForm(null)
      await invalidate()
    },
    onError: (error) => {
      setErrors(fieldErrors(error))
      toast.error(errorMessage(error))
    },
  })
  const action = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: 'test' | 'activate' | 'deactivate' }) =>
      session.api
        .post<{ ok?: boolean; message?: string }>(`/admin/notifications/providers/${id}/${kind}`)
        .then((r) => ({ ...r, action: kind })),
    onSuccess: async (r) => {
      if (r.action === 'test') {
        if (r.ok) toast.success(r.message ?? 'Test réussi')
        else toast.error(r.message ?? 'Test échoué')
      } else toast.success(r.action === 'activate' ? 'Fournisseur activé' : 'Fournisseur désactivé')
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const open = (p?: Provider) => {
    setErrors({})
    setForm(
      p
        ? {
            id: p.id,
            driver: p.driver,
            label: p.label,
            config: p.config,
            secrets: {},
            secretsSet: p.secretsSet,
          }
        : { driver: 'smtp', label: '', config: {}, secrets: {}, secretsSet: [] }
    )
  }
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (form) save.mutate(form)
  }

  const group = (kind: 'email' | 'sms') =>
    (providers.data?.data ?? []).filter((p) => p.kind === kind)

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Fournisseurs email et SMS"
        description="Serveur SMTP pour les courriels, API SMS d’Orange pour les alertes importantes. Un fournisseur s’active après un test réussi ; un seul est actif par type."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => open()}>
            Ajouter un fournisseur
          </Button>
        }
      />
      {providers.isError && <Alert tone="danger">{errorMessage(providers.error)}</Alert>}
      <div className="stack" style={{ gap: 'var(--space-6)' }}>
        {(['email', 'sms'] as const).map((kind) => (
          <Card key={kind}>
            <CardHeader title={kind === 'email' ? 'Courriels' : 'SMS'} />
            {providers.isPending ? (
              <CardBody>Chargement…</CardBody>
            ) : group(kind).length === 0 ? (
              <Empty
                icon={<PlugZap aria-hidden="true" />}
                title="Aucun fournisseur"
                description={
                  kind === 'email'
                    ? 'Les courriels partent par le serveur SMTP configuré sur le serveur (.env).'
                    : 'Sans fournisseur actif, les SMS sont simulés hors production et refusés en production.'
                }
              />
            ) : (
              <ul className="provider-list">
                {group(kind).map((p) => (
                  <li key={p.id}>
                    <div className="stack grow" style={{ gap: 'var(--space-1)' }}>
                      <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        <strong>{p.label}</strong>
                        <Badge variant="outline">{p.driverLabel}</Badge>
                        {p.isActive ? (
                          <Badge variant="success">Actif</Badge>
                        ) : (
                          <Badge variant="neutral">Inactif</Badge>
                        )}
                      </span>
                      {p.missing.length > 0 && (
                        <span className="field-error">À compléter : {p.missing.join(', ')}</span>
                      )}
                      {p.lastTest ? (
                        <span className={p.lastTest.ok ? 'muted' : 'field-error'}>
                          {p.lastTest.ok ? (
                            <CircleCheck aria-hidden="true" size={14} />
                          ) : (
                            <CircleAlert aria-hidden="true" size={14} />
                          )}{' '}
                          Test du {when(p.lastTest.at)} : {p.lastTest.message}
                        </span>
                      ) : (
                        <span className="muted">Jamais testé</span>
                      )}
                    </div>
                    <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                      <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => open(p)}>
                        Modifier
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<Send />}
                        loading={
                          action.isPending &&
                          action.variables?.id === p.id &&
                          action.variables.kind === 'test'
                        }
                        onClick={() => action.mutate({ id: p.id, kind: 'test' })}
                      >
                        Tester
                      </Button>
                      {p.isActive ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => action.mutate({ id: p.id, kind: 'deactivate' })}
                        >
                          Désactiver
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={!p.lastTest?.ok || p.missing.length > 0}
                          onClick={() => action.mutate({ id: p.id, kind: 'activate' })}
                        >
                          Activer
                        </Button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ))}
      </div>

      <Modal
        open={form !== null}
        onClose={() => setForm(null)}
        title={form?.id ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
        description={form && drivers ? drivers[form.driver].description : undefined}
        footer={
          <>
            <Button variant="secondary" onClick={() => setForm(null)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" form="provider-form" loading={save.isPending}>
              Enregistrer
            </Button>
          </>
        }
      >
        {form && drivers && (
          <form id="provider-form" className="stack" onSubmit={submit} noValidate>
            {!form.id && (
              <Select
                label="Type de fournisseur"
                value={form.driver}
                options={Object.entries(drivers).map(([value, d]) => ({
                  value,
                  label: `${d.label} (${d.kind === 'email' ? 'courriel' : 'SMS'})`,
                }))}
                onChange={(e) =>
                  setForm({
                    ...form,
                    driver: e.target.value as NotificationProviderDriver,
                    config: {},
                    secrets: {},
                  })
                }
              />
            )}
            <Input
              label="Nom"
              required
              value={form.label}
              error={errors.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
            {drivers[form.driver].fields.map((f) =>
              f.secret ? (
                <Input
                  key={f.key}
                  label={f.label}
                  type="password"
                  autoComplete="new-password"
                  required={f.required && !form.secretsSet.includes(f.key)}
                  placeholder={
                    form.secretsSet.includes(f.key)
                      ? '•••••••• (défini — saisir pour remplacer)'
                      : ''
                  }
                  value={form.secrets[f.key] ?? ''}
                  error={errors[`secrets.${f.key}`]}
                  hint={f.help}
                  onChange={(e) =>
                    setForm({ ...form, secrets: { ...form.secrets, [f.key]: e.target.value } })
                  }
                />
              ) : (
                <Input
                  key={f.key}
                  label={f.label}
                  required={f.required}
                  value={form.config[f.key] ?? ''}
                  error={errors[`config.${f.key}`]}
                  hint={f.help}
                  onChange={(e) =>
                    setForm({ ...form, config: { ...form.config, [f.key]: e.target.value } })
                  }
                />
              )
            )}
          </form>
        )}
      </Modal>
    </>
  )
}
